import posthog from 'posthog-js';
import { installUnhandledExceptionCapture } from './exceptionCapture';
import { isPostHogConfigured } from './posthogEnv';
import { createPostHogOptions } from './posthogOptions';

type PostHogClient = typeof posthog;

/**
 * Initialize PostHog before React renders so startup errors are observable.
 * Returns null when the project key is missing. Staging and dev keep the
 * same host, pageview, and critical-only settings as before.
 */
export function startBrowserPostHog(): PostHogClient | null {
  const debug =
    import.meta.env.DEV || import.meta.env.VITE_ANALYTICS_DEBUG === 'true';
  if (!isPostHogConfigured()) {
    if (debug) {
      console.warn('PostHog not configured - analytics will be disabled');
      console.warn(
        'Set VITE_PUBLIC_POSTHOG_KEY environment variable to enable PostHog',
      );
    }
    return null;
  }
  const posthogApiKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
  if (typeof posthogApiKey !== 'string' || posthogApiKey.trim().length === 0) {
    return null;
  }

  const posthogHost =
    import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';
  const isCriticalOnlyMode =
    import.meta.env.VITE_ANALYTICS_CRITICAL_ONLY === 'true';

  if (debug) {
    console.log('PostHog configured:', {
      host: posthogHost,
      keyLength: posthogApiKey.length,
      criticalOnlyMode: isCriticalOnlyMode,
    });
  }

  const options = createPostHogOptions({
    posthogHost,
    isCriticalOnlyMode,
    secureCookie:
      typeof window !== 'undefined' && window.location?.protocol === 'https:',
    debug,
  });

  try {
    if (!posthog.__loaded) {
      posthog.init(posthogApiKey, options);
    }
    installUnhandledExceptionCapture(posthog);
    return posthog;
  } catch (error) {
    if (debug) {
      console.warn('PostHog init failed', error);
    }
    return null;
  }
}

export const posthogClient = startBrowserPostHog();
